import React, { useState, useEffect } from 'react';
import api from '../../api';
import './UserPermissionsPage.css';

export default function UserPermissionsPage() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceTypes, setResourceTypes] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState({ name: '', email: '' });
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [newPermission, setNewPermission] = useState({
    resourceType: '',
    accessLevel: 'view',
    resourceId: null
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadUserPermissions(selectedUser.id);
    }
  }, [selectedUser]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => 
        (user.name && user.name.toLowerCase().includes(query)) ||
        (user.emailAddress && user.emailAddress.toLowerCase().includes(query))
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const loadData = async () => {
    try {
      const [usersRes, typesRes, boardsRes] = await Promise.all([
        api.get('/user-permissions/users'),
        api.get('/resource-types'),
        api.get('/boards')
      ]);
      const usersList = usersRes.data || [];
      setUsers(usersList);
      setFilteredUsers(usersList);
      setResourceTypes(typesRes.data || []);
      setBoards(boardsRes.data || []);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load data:', err);
      alert('Failed to load data. Please try again.');
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const res = await api.get(`/user-permissions/user/${userId}`);
      setUserPermissions(res.data || []);
    } catch (err) {
      console.error('Failed to load user permissions:', err);
      alert('Failed to load user permissions.');
    }
  };

  const handleAddPermission = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPermission.resourceType) {
      alert('Please select a user and resource type');
      return;
    }

    try {
      await api.post('/user-permissions', {
        userId: selectedUser.id,
        resourceType: newPermission.resourceType,
        resourceId: newPermission.resourceId || null,
        accessLevel: newPermission.accessLevel
      });
      
      // Reload permissions
      await loadUserPermissions(selectedUser.id);
      
      // Reset form
      setNewPermission({ resourceType: '', accessLevel: 'view', resourceId: null });
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to add permission:', err);
      alert(err.response?.data?.error || 'Failed to add permission. Please try again.');
    }
  };

  const handleUpdatePermission = async (permissionId, updates) => {
    try {
      await api.put(`/user-permissions/${permissionId}`, updates);
      
      // Reload permissions
      await loadUserPermissions(selectedUser.id);
      setEditingPermission(null);
    } catch (err) {
      console.error('Failed to update permission:', err);
      alert(err.response?.data?.error || 'Failed to update permission. Please try again.');
    }
  };

  const handleEditPermission = (permission) => {
    setEditingPermission(permission);
    setShowAddForm(false);
  };

  const handleDeletePermission = async (permissionId) => {
    if (!window.confirm('Are you sure you want to remove this permission?')) {
      return;
    }

    try {
      await api.delete(`/user-permissions/${permissionId}`);
      
      // Reload permissions
      await loadUserPermissions(selectedUser.id);
    } catch (err) {
      console.error('Failed to delete permission:', err);
      alert(err.response?.data?.error || 'Failed to delete permission. Please try again.');
    }
  };

  const handleEditUser = () => {
    if (!selectedUser) return;
    setEditingUser({
      name: selectedUser.name || '',
      email: selectedUser.emailAddress || ''
    });
    setShowEditUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      // Update user via tasks/people endpoint
      await api.put(`/tasks/people/${selectedUser.id}`, {
        name: editingUser.name,
        emailAddress: editingUser.email
      });
      
      // Reload users and update selected user
      const usersRes = await api.get('/user-permissions/users');
      const updatedUsers = usersRes.data || [];
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      // Update selected user
      const updated = updatedUsers.find(u => u.id === selectedUser.id);
      if (updated) {
        setSelectedUser(updated);
      }
      
      setShowEditUserModal(false);
      alert('User updated successfully');
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(err.response?.data?.error || 'Failed to update user. Please try again.');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(`Reset password for ${selectedUser.name || selectedUser.emailAddress}? They will need to set a new password on their next login.`)) {
      return;
    }

    setResettingPassword(true);
    try {
      await api.post('/auth/admin/reset-password', {
        userId: selectedUser.id
      });
      
      alert('Password reset successfully. The user can now login with any password and will be prompted to set a new one.');
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div className="user-permissions-page">
      <h2>User Permissions Management</h2>
      
      <div className="permissions-layout">
        {/* User List */}
        <div className="users-panel">
          <h3>Users</h3>
          <div className="user-search">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="users-list">
            {filteredUsers.map(user => (
              <div
                key={user.id}
                className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                onClick={() => setSelectedUser(user)}
              >
                <div className="user-name">{user.name || 'No name'}</div>
                <div className="user-email">{user.emailAddress}</div>
              </div>
            ))}
            {filteredUsers.length === 0 && searchQuery && (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                No users found
              </div>
            )}
          </div>
        </div>

        {/* Permissions Panel */}
        <div className="permissions-panel">
          {selectedUser ? (
            <>
              <div className="permissions-header">
                <div>
                  <h3>Permissions for {selectedUser.name || selectedUser.emailAddress}</h3>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.25rem' }}>
                    {selectedUser.emailAddress}
                  </div>
                </div>
                <div>
                  <button
                    className="edit-user-btn"
                    onClick={handleEditUser}
                  >
                    Edit Profile
                  </button>
                  <button
                    className="add-permission-btn"
                    onClick={() => setShowAddForm(!showAddForm)}
                  >
                    {showAddForm ? '−' : '+'} Add Permission
                  </button>
                </div>
              </div>

              {showAddForm && (
                <form onSubmit={handleAddPermission} className="add-permission-form">
                  <div className="form-row">
                    <label>Resource Type:</label>
                    <select
                      value={newPermission.resourceType}
                      onChange={(e) => {
                        // Reset resourceId when resource type changes
                        setNewPermission({ 
                          ...newPermission, 
                          resourceType: e.target.value,
                          resourceId: null 
                        });
                      }}
                      required
                    >
                      <option value="">Select resource type...</option>
                      {resourceTypes.map(type => (
                        <option key={type.id} value={type.type_name}>
                          {type.type_name} {type.description ? `- ${type.description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Show board selector if "board" is selected */}
                  {newPermission.resourceType === 'board' && (
                    <div className="form-row">
                      <label>Board (optional - leave blank for all boards):</label>
                      <select
                        value={newPermission.resourceId || ''}
                        onChange={(e) => setNewPermission({ 
                          ...newPermission, 
                          resourceId: e.target.value ? parseInt(e.target.value) : null 
                        })}
                      >
                        <option value="">All Boards (type-level access)</option>
                        {boards.map(board => (
                          <option key={board.id} value={board.id}>
                            {board.name} {board.team_name ? `(${board.team_name})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="form-row">
                    <label>Access Level:</label>
                    <select
                      value={newPermission.accessLevel}
                      onChange={(e) => setNewPermission({ ...newPermission, accessLevel: e.target.value })}
                      required
                    >
                      <option value="view">View (read-only)</option>
                      <option value="edit">Edit (can modify)</option>
                      <option value="admin">Admin (full access)</option>
                    </select>
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="save-btn">Add Permission</button>
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowAddForm(false);
                        setNewPermission({ resourceType: '', accessLevel: 'view', resourceId: null });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="permissions-list">
                {userPermissions.length === 0 ? (
                  <p className="no-permissions">No permissions assigned</p>
                ) : (
                  <table className="permissions-table">
                    <thead>
                      <tr>
                        <th>Resource Type</th>
                        <th>Resource</th>
                        <th>Access Level</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userPermissions.map(permission => {
                        // Find board name if this is a board-specific permission
                        const boardName = permission.resource_type === 'board' && permission.resource_id
                          ? boards.find(b => b.id === permission.resource_id)?.name
                          : null;
                        
                        const isEditing = editingPermission?.id === permission.id;
                        
                        return (
                          <tr key={permission.id}>
                            <td>{permission.resource_type}</td>
                            <td>
                              {isEditing && permission.resource_type === 'board' ? (
                                <select
                                  value={editingPermission.resource_id || ''}
                                  onChange={(e) => setEditingPermission({
                                    ...editingPermission,
                                    resource_id: e.target.value ? parseInt(e.target.value) : null
                                  })}
                                  className="access-level-select"
                                  style={{ width: '100%' }}
                                >
                                  <option value="">All Boards (type-level access)</option>
                                  {boards.map(board => (
                                    <option key={board.id} value={board.id}>
                                      {board.name} {board.team_name ? `(${board.team_name})` : ''}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                permission.resource_id
                                  ? (boardName ? `${boardName}${boards.find(b => b.id === permission.resource_id)?.team_name ? ` (${boards.find(b => b.id === permission.resource_id).team_name})` : ''}` : `ID: ${permission.resource_id}`)
                                  : <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontStyle: 'italic' }}>All</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <select
                                  value={editingPermission.access_level}
                                  onChange={(e) => setEditingPermission({
                                    ...editingPermission,
                                    access_level: e.target.value
                                  })}
                                  className="access-level-select"
                                  style={{ width: '100%' }}
                                >
                                  <option value="view">View</option>
                                  <option value="edit">Edit</option>
                                  <option value="admin">Admin</option>
                                </select>
                              ) : (
                                <select
                                  value={permission.access_level}
                                  onChange={(e) => handleUpdatePermission(permission.id, { accessLevel: e.target.value })}
                                  className="access-level-select"
                                >
                                  <option value="view">View</option>
                                  <option value="edit">Edit</option>
                                  <option value="admin">Admin</option>
                                </select>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    className="save-btn"
                                    onClick={() => handleUpdatePermission(permission.id, {
                                      accessLevel: editingPermission.access_level,
                                      resourceId: editingPermission.resource_id
                                    })}
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="cancel-btn"
                                    onClick={() => setEditingPermission(null)}
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  {permission.resource_type === 'board' && (
                                    <button
                                      className="edit-btn"
                                      onClick={() => handleEditPermission(permission)}
                                      title="Edit board access"
                                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                                    >
                                      Edit
                                    </button>
                                  )}
                                  <button
                                    className="delete-btn"
                                    onClick={() => handleDeletePermission(permission.id)}
                                    title="Remove permission"
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a user to view and manage their permissions</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="edit-user-modal" onClick={() => setShowEditUserModal(false)}>
          <div className="edit-user-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User Profile</h3>
            <form onSubmit={handleSaveUser} className="edit-user-form">
              <div>
                <label>Name:</label>
                <input
                  type="text"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label>Email:</label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  required
                />
              </div>
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  style={{ marginBottom: '0.5rem', width: '100%' }}
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '1rem' }}>
                  User will need to set a new password on next login
                </div>
              </div>
              <div className="edit-user-form-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowEditUserModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

